/*
 * ## Adding a New Admin Entity — Step-by-Step
 *
 * 1. FIND BACKEND ROUTES
 *    Look in: src/service/<entity>/controller.go (backend repo)
 *    Note: HTTP method, path, which router it's registered on (super vs authenticated)
 *
 * 2. FIND BACKEND TYPES
 *    Look in: src/service/<entity>/types.go (backend repo)
 *    Map Go types to TypeScript: string → string, uuid.UUID → string,
 *    *string → string | undefined, sql.NullTime → string | null
 *
 * 3. CREATE FEATURE FILES
 *    src/features/<entity>/
 *      types.ts   — TypeScript types (mirror of Go types)
 *      api.ts     — apiFetch wrappers for each endpoint
 *      hooks.ts   — React Query hooks (useQuery for lists, useMutation for writes)
 *
 * 4. ADD ENDPOINTS (if not already in endpoints.ts)
 *    src/api/endpoints.ts — add list and detail(id) entries
 *
 * 5. CREATE PAGE
 *    src/pages/mais/<Entity>Page.tsx — use AdminCard, AdminDrawer, AdminListRow, AdminBreadcrumb
 *
 * 6. REGISTER ROUTE
 *    src/router/index.ts — add createRoute under authLayout, add to routeTree
 *
 * 7. ADD CARD ON MAIS PAGE
 *    src/pages/MaisPage.tsx — add AdminCard pointing to new route
 */

import { apiFetch } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import type {
  Product,
  CreateProductPayload,
  UpdateProductPayload,
  PageResponse,
  ProductPageQuery,
} from "./types";
import {
  normalizeProduct,
  normalizeProductPage,
  type ProductPageResponse,
  type ProductResponse,
} from "./normalization";

export function toBackendProductUnit(
  unit: CreateProductPayload["default_unit"],
) {
  switch (unit) {
    case "kg":
      return "KG";
    case "cx_un":
      return "CAIXA";
    case "bdj":
      return "BDJ";
    case "un":
      return "UNIDADE";
    case "sc_kg":
    case "cx_kg":
    default:
      return "KG";
  }
}

export function listProducts(
  limit = 50,
  offset = 0,
): Promise<PageResponse<Product>> {
  return apiFetch<ProductPageResponse>(
    `${ENDPOINTS.products.list}?limit=${limit}&offset=${offset}`,
  ).then(normalizeProductPage);
}

export function listProductPage(
  query: ProductPageQuery,
  signal?: AbortSignal,
): Promise<PageResponse<Product>> {
  const params = new URLSearchParams({
    limit: String(query.limit),
    offset: String(query.offset),
  });
  const search = query.search.trim();
  if (search) params.set("search", search);
  if (query.category) params.set("category", query.category);

  return apiFetch<ProductPageResponse>(`${ENDPOINTS.products.list}?${params}`, {
    signal,
  }).then(normalizeProductPage);
}

export function createProduct(payload: CreateProductPayload): Promise<Product> {
  return apiFetch<ProductResponse>(ENDPOINTS.products.list, {
    method: "POST",
    body: JSON.stringify({
      ...payload,
      default_unit: toBackendProductUnit(payload.default_unit),
    }),
  }).then(normalizeProduct);
}

export function updateProduct(
  id: string,
  payload: UpdateProductPayload,
): Promise<Product> {
  const body = {
    ...payload,
    default_unit: payload.default_unit
      ? toBackendProductUnit(payload.default_unit)
      : undefined,
  };
  return apiFetch<ProductResponse>(ENDPOINTS.products.detail(id), {
    method: "PATCH",
    body: JSON.stringify(body),
  }).then(normalizeProduct);
}

export function deleteProduct(id: string): Promise<void> {
  return apiFetch(ENDPOINTS.products.detail(id), { method: "DELETE" });
}
